import React, { memo, useContext, useEffect, useRef } from 'react';
import InputControl from './InputControl';
import { createUseStyles } from 'react-jss';
import { HandleChangeVisible, IFormField, SelectValue } from '../schema/interface';
import { useNumberOfToggle } from '../hooks/useNumberOfToggle';
import useAlgorithm from '../hooks/useAlgorithm';
import FormStructureContext from '../context/FormStructureContext';

const styles = createUseStyles({
    label: ({ isGroupField }) => ({
        display: 'block',
        marginBottom: '0.5em',
        fontWeight: isGroupField && 'bold',
        fontSize: isGroupField && '15px',
        padding: isGroupField && '15px'
        // whiteSpace: 'nowrap'
    }),
    description: {
        fontSize: '1em',
        marginTop: '.2em',
        color: 'grey',
        '& *': {
            fontFamily: 'inherit !important',
            fontSize: '13px !important',
            fontWeight: 'normal !important',
            background: 'transparent !important',
            color: 'black !important'
        }
    },
    wrapper: ({ isBoolean }) => ({
        display: 'flex',
        justifyContent: isBoolean && 'flex-end',
        alignItems: isBoolean && 'center',
        flexDirection: isBoolean ? 'row-reverse' : 'column'
    }),
    formGroup: ({ isGroupField, hasParent }) => ({
        // display: 'inline-block',
        marginBottom: '1rem',
        boxShadow: isGroupField && !hasParent && 'rgb(35 39 51 / 10%) 0px 2px 8px 0px',
        border: isGroupField && !hasParent && '1.5px solid rgb(233, 238, 242)',
        borderRadius: isGroupField && !hasParent && '6px',
        // paddingInlineEnd: !isGroupField && hasParent && '20px',
        marginInlineStart: !isGroupField && hasParent && '20px'
        // marginInlineEnd: isGroupField && !hasParent && '15px',
    }),
    toggleContainer: {
        gridColumn: ({ column }) => column && `span ${column}`,
        marginInlineEnd: ({ isGroupField, hasParent }) => !isGroupField && hasParent && '20px'
        // gridColumn: ({ height }) => height >= 44 && `span ${2}`
    },
    longField: {
        gridColumn: 'span 2',
        marginInlineEnd: ({ isGroupField, hasParent }) => !isGroupField && hasParent && '20px'
    },
    '@media (max-width: 555px)': {
        longField: {
            gridColumn: 'span 1'
        }
    },
    wholeLineFormGroup: {
        marginBottom: '1rem',
        paddingInlineEnd: '13px',
        display: 'block',
        width: '100%'
    },
    break: {
        width: '100%',
        margin: '0',
        border: 'none',
        // gridColumn: ({ rtl }) => (rtl ? 'span 2 / -1' : 'span 2')
        gridColumn: 'span 2 / -1'
    },
    img: {
        maxHeight: '230px',
        display: 'block',
        marginBottom: '7px',
        width: '100%'
    }
});
type IInputContainerProps = {
    field: IFormField;
    prevField: IFormField;
    value: string | string[] | SelectValue;
    uploadMedia: Function;
    handleChangeVisible?: HandleChangeVisible;
    formValues?: { [key: string]: string | string[] | SelectValue };
    noAccess?: boolean;
    rtl?: boolean;
};
const InputContainer = ({
    field,
    prevField,
    value,
    uploadMedia,
    handleChangeVisible,
    formValues,
    noAccess,
    rtl
}: IInputContainerProps) => {
    const { description, label, data_type, metadata, presentation, name } = field;
    let breakLine = false;
    const isGroupField = field?.data_type === 'group';
    const hasParent = !!field?.parent;
    const { isToggle, column } = useNumberOfToggle({ field });
    if (field?.presentation) {
        const presentation = field?.presentation;
        const group = presentation.link?.group;
        const prevGroup = prevField?.presentation?.link?.group;
        const newLine = presentation.hasPrecedingBreakLine;
        if ((group && group !== prevGroup) || newLine || (group && !prevGroup)) {
            breakLine = true;
        }
    }
    const isBoolean = presentation?.selectType === 2;
    const isLongField = data_type === 'text';
    const { fetchedFormStructure: formStructure } = useContext(FormStructureContext);
    const firstRender = useRef(true);
    const { handleAlgorithmCode } = useAlgorithm({
        formStructure,
        formValues,
        handleChangeVisible
    });
    // execute algorithm in first render
    useEffect(() => {
        if (firstRender?.current && formStructure?.fields && formValues) {
            firstRender.current = false;
            return handleAlgorithmCode();
        }
    }, [formStructure, firstRender?.current, formValues]);

    const classes = styles({
        breakLine,
        isBoolean,
        isGroupField,
        hasParent,
        column,
        isToggle,
        rtl
    });
    const dynamicClass = isLongField
        ? classes.longField
        : isGroupField
        ? 'blockWrapper'
        : isToggle
        ? classes.toggleContainer
        : undefined;
    if (presentation.invisible || !label) {
        return null;
    }
    return (
        <>
            {breakLine && <hr className={`${classes.break} breakLine `} />}
            <div className={`${dynamicClass} ${classes.formGroup}`}>
                <div className={classes.wrapper}>
                    <label className={classes.label} htmlFor={name}>
                        {label}
                    </label>
                    {field?.presentation?.mediaUrl && (
                        <img
                            className={classes.img}
                            src={field.presentation.mediaUrl}
                            alt={field?.name}
                        />
                    )}
                    <InputControl
                        name={name}
                        type={data_type}
                        value={value}
                        metadata={metadata}
                        presentation={presentation}
                        uploadMedia={uploadMedia}
                        label={label}
                        field={field}
                        handleChangeVisible={handleChangeVisible}
                        formValues={formValues}
                        noAccess={noAccess}
                    />
                    {description && (
                        <p
                            className={classes.description}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
const compareFn = (prevProps: IInputContainerProps, nextProps: IInputContainerProps) => {
    if (
        prevProps.value !== nextProps.value ||
        prevProps.field.presentation !== nextProps.field.presentation
    ) {
        return false;
    }
    return true;
};
export default memo(InputContainer, compareFn);
